import {
  Grid, Checkbox, TableContainer, Table,
  TableBody, TableRow, TableCell,
  Box, Tooltip, useMediaQuery, useTheme,
  Typography,
  Chip,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { OrderStatus } from '../../../enums/OrderStatus';
import { applyRoundAndFormat } from '../../../utils';
import TextWithOverflowTooltip from '../../../components/shared/TextWithOverflowTooltip';
import { isOrderCategory, OrderCategoriesTranslationMap } from '../../../enums/OrderCategories';
import './OrderDetails.css';
import NoProductImage from '../../../assets/images/NoProductImage.png';
import { useTranslation } from 'react-i18next';
import { ContainerFillResult, OrderDetails, OrderItemDetails } from '../../../app/services/api/orderManagementClient';
import { createRef, useContext } from 'react';
import { UserContext } from '../../../components/shared/useUser';
import { useNavigate } from 'react-router-dom';
import { starbucksFranchises } from '../../../enums/FranchiseNames';
import { GroupedTableVirtuoso } from 'react-virtuoso';
import { QuantityCell, SaveStatus } from './QuantityCell';
import { EmptyTable } from '../../../components/shared/EmptyTable';

enum PriceOverrideReason {
  PromoOffer = 1,
  AccountManagerDecision = 2
}

const PriceOverrideReasonText: Record<number, string> = {
  [PriceOverrideReason.PromoOffer]: 'Promo Offer',
  [PriceOverrideReason.AccountManagerDecision]: 'Account Manager Decision',
};

interface tableProps {
  filteredItems: OrderItemDetails[],
  orderDetails: OrderDetails | null | undefined,
  selectedItemIds: number[],
  handleCheckboxChange: (itemId: number, isChecked: boolean) => void,
  canDeleteOrderItem: () => boolean,
  canChangeOrderQuantity: () => boolean,
  currencyCode: string | undefined,
  canEditOrder: () => boolean,
  handleEditClick: (item: OrderItemDetails) => void,
  deliveryChargeTotal: string,
  isQuantityError: (item: OrderItemDetails) => boolean,
  quantityError: (item: OrderItemDetails) => string | null,
  handleConfirmDeletion: (itemId: number, itemNumber: number) => Promise<void>,
  autosaveItem: (setSaveStatus?: (status: SaveStatus) => void) => Promise<void>,
  handleOpenDialogForDeletion: (itemId: number, itemNumber: number) => void,
  getQuantity: (itemId: number, fallback: number) => number;
  setQuantityInRef: (itemId: number, value: number) => void;
  containerCharge: string;
  containerData: ContainerFillResult | null;
  computeContainerCount: (d: ContainerFillResult | null) => number;
  clearFilters: () => void;
  productSearch: string;
  handleBrowseCatalogueClick: () => void; 
}

const OrderTable: React.FC<tableProps> = ({ filteredItems, orderDetails, selectedItemIds, handleCheckboxChange, canDeleteOrderItem, canChangeOrderQuantity, currencyCode, canEditOrder, handleEditClick, deliveryChargeTotal, isQuantityError, quantityError, handleConfirmDeletion, autosaveItem, handleOpenDialogForDeletion, getQuantity, setQuantityInRef, containerCharge, containerData, computeContainerCount, clearFilters, productSearch, handleBrowseCatalogueClick }) => {
  const { t } = useTranslation('orderDetails');
  const { hasPermissionTo, selectedStore } = useContext(UserContext);
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const navigate = useNavigate();

  const isStarBucksRetail = starbucksFranchises.includes(selectedStore?.franchiseName ?? '');
  const showMessageWhenNoCost = isStarBucksRetail && orderDetails?.status != 'Order Confirmed';

  const totalCost = 0;

  const categoryMap: Record<string, true> = {};

  for (const item of filteredItems) {
    categoryMap[item.category] = true;
  }

  const categories = Object.keys(categoryMap).sort((a, b) =>
    a.localeCompare(b),
  );

  const groupCounts = categories.map(category =>
    filteredItems.filter(item => item.category === category).length,
  );

  if (filteredItems.length === 0) {
    return <EmptyTable
      text={t('items')}
      searchTerm={productSearch}
      onClear={clearFilters}
      onAdd={handleBrowseCatalogueClick}
      addItemText={t('browseCatalogue')}
      onAddIsCreating={false} 
      withPaginationShown={false} />;
  }

  return (
    <TableContainer
      component={'div'}
      sx={{
        overflowX: 'auto',
        maxWidth: '100%',
      }}
    >
      <GroupedTableVirtuoso
        style={{
          height: 'max(95vh, 400px)',
          transform: 'none', // <— prevents Virtuoso wrapper from creating a new stacking context
          border: '0px',
        }}
        overscan={20}
        groupCounts={groupCounts}
        components={{
          Table: (props) => (
            <Table
              {...props}
              size="small"
              sx={{
                tableLayout: 'auto',
                minWidth: 900,
                border: '0px',
              }}
              aria-label="order form table"
            />
          ),
          TableRow: TableRow,
        }}
        fixedHeaderContent={() => (
          <TableRow sx={{
            borderBottom: '10px', margin: 0, position: 'sticky',
            top: 0,
          }}>
            <TableCell
              sx={{
                width: '60%',
                minWidth: '60%',
                position: 'sticky',
                top: 0,
                zIndex: 3, // ensure it stays above content
                backgroundColor: theme.palette.common.white,
                borderBottom: '0px solid',
                borderColor: theme.palette.common.white,
                textAlign: 'center',
              }}>
              {t('itemDetails')}
            </TableCell>
            <TableCell sx={{
              position: 'sticky',
              top: 0,
              zIndex: 3, // ensure it stays above content
              backgroundColor: theme.palette.common.white,
              borderBottom: '0px solid',
              borderColor: theme.palette.common.white,
              textAlign: 'center',
            }}>
            </TableCell>
            <TableCell sx={{
              width: '10%',
              position: 'sticky',
              top: 0,
              zIndex: 3, // ensure it stays above content
              backgroundColor: theme.palette.common.white,
              borderBottom: '0px solid',
              borderColor: theme.palette.common.white,
              textAlign: 'center',
            }}>
              {t('ordered')}
            </TableCell>
            <TableCell sx={{
              width: '9%',
              position: 'sticky',
              top: 0,
              zIndex: 3, // ensure it stays above content
              backgroundColor: theme.palette.common.white,
              borderBottom: '0px solid',
              borderColor: theme.palette.common.white,
              textAlign: 'center',
            }}>
              {t('cost')}
            </TableCell>
            <TableCell sx={{
              width: '9%',
              position: 'sticky',
              top: 0,
              zIndex: 3, // ensure it stays above content
              backgroundColor: theme.palette.common.white,
              borderBottom: '0px solid',
              borderColor: theme.palette.common.white,
              textAlign: 'center',
            }}>
              {t('actions')}
            </TableCell>
          </TableRow>
        )}
        groupContent={(groupIndex) => {
          const category = categories[groupIndex];
          return (
            <TableCell
              key={`category-${category}`}
              colSpan={6}
              sx={{
                backgroundColor: theme.palette.primary.main,
                px: 3,
                py: 2,
                position: 'sticky',
                top: 38,
                zIndex: 1,
              }}
            >
              <Typography variant="textSM"
                color="white">
                {isOrderCategory(category)
                  ? t(
                    OrderCategoriesTranslationMap[
                      category as keyof typeof OrderCategoriesTranslationMap
                    ],
                  )
                  : category}
              </Typography>
            </TableCell>
          );
        }}
        itemContent={(itemIndex, groupIndex) => {
          const category = categories[groupIndex];
          const offset = groupCounts.slice(0, groupIndex).reduce((a, b) => a + b, 0);
          const item = filteredItems.filter(x => x.category == category)?.[itemIndex - offset];
          const priceCellRef = createRef<HTMLSpanElement>();

          const initialQuantity = getQuantity(item.itemId, item.quantity);
          const initialLinePrice = applyRoundAndFormat(item.price * initialQuantity, currencyCode);

          if (!item) {
            return null;
          }

          return (
            <>
              <TableCell sx={{
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '60%',
                minWidth: '60%', paddingLeft: 0,
              }}>
                <Grid sx={{
                  ...(((item.amendmentDetails || parseInt(item.priceOverrideReason ?? '') > 0) && orderDetails?.status === 'Amended')
                    ? { borderLeft: '4px solid #FFD600 !important' } // force yellow border
                    : {}),
                  display: 'flex', alignItems: 'center', paddingLeft: '8px',
                }}>
                  <Grid item
                    xs="auto">
                    <Checkbox
                      checked={selectedItemIds.includes(item.id)}
                      onChange={(event) => handleCheckboxChange(item.id, event.target.checked)}
                      disabled={!canDeleteOrderItem()}
                    />
                  </Grid>
                  <Grid item
                    sx={{ marginRight: '15px' }}
                    onClick={
                      hasPermissionTo(['ProductDetailView'])
                        ? () => navigate(`/ofd/products/${item.sku}`, {
                          state: {
                            orderId: orderDetails?.id,
                            fromOrderPage: true,
                            quantity: getQuantity(item.itemId, item.quantity),
                            canChangeOrderQuantity: canChangeOrderQuantity(),
                            orderItemId: item.id,
                          },
                        })
                        : undefined
                    }>
                    <Box
                      style={{
                        width: '100px',
                        height: isLargeScreen ? '75px' : '100px',
                        justifyContent: 'center',
                        backgroundColor: (item?.thumbnailUrl ?? '') != '' ? theme.palette.common.white : theme.palette.custom.gray[200],
                        borderRadius: '8px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        border: '1px solid #ddd',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: 'contain',
                        imageRendering: '-webkit-optimize-contrast',
                        backgroundImage: `url(${item?.thumbnailUrl && item?.thumbnailUrl.trim() !== '' ? item.thumbnailUrl : NoProductImage})`,
                      }}
                    />
                  </Grid>


                  <Grid item
                    sx={{ width: '65%' }}
                  >
                    <TextWithOverflowTooltip text={
                      item.name + (item.amendmentDetails && orderDetails?.status === 'Amended'
                        ? ` (${item.amendmentDetails})`
                        : '')
                    }
                    />
                    <TextWithOverflowTooltip text={item.sku} />
                    <Typography variant='textXS'
                      display="block">
                      {t('orderableUOM')} {item.orderableUom} ({item.packSize}
                      {item.primaryUom})
                    </Typography>
                    <Typography variant='textXS'
                      display="block">
                      {t('primaryUOM')} {item.primaryUom}
                    </Typography>
                    <Box>
                      <Tooltip title={`${t('stockOnHand')}: ${item.stockOnHand}`}
                        arrow>
                        <Chip
                          size="small"
                          color="primary"
                          variant='outlined'
                          label={`${t('stockOnHandABR')} ${item.stockOnHand}`}
                          sx={{ mr: 2 }}
                        />
                      </Tooltip>
                      <Tooltip title={`${t('stockOnOrder')}: ${item.stockOnOrder}`}
                        arrow>
                        <Chip
                          size="small"
                          color="warning"
                          variant='outlined'
                          label={`${t('StockOnOrderABR')} ${item.stockOnOrder}`}
                          sx={{ mr: 2 }}
                        />
                      </Tooltip>
                    </Box>
                  </Grid>
                </Grid>
              </TableCell >

              <TableCell sx={{ width: '8%' }}>
                {(showMessageWhenNoCost || (!item.priceOverrideReason && item.price === 0)) ?
                  <TextWithOverflowTooltip text='' />
                  :
                  (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {item.priceOverrideOriginalPrice
                        ? (
                          <>
                            <Tooltip
                              title={
                                PriceOverrideReasonText[Number(item.priceOverrideReason)] ||
                                  item.priceOverrideReason ||
                                  ''
                              }
                            >
                              <span style={{
                                textDecoration: 'line-through',
                                color: theme.palette.text.disabled,
                                cursor: item.priceOverrideReason ? 'pointer' : 'default',
                              }}>
                                {applyRoundAndFormat(item.priceOverrideOriginalPrice, currencyCode)}
                              </span>
                            </Tooltip>
                            <span style={{ marginLeft: 6 }}>
                              {applyRoundAndFormat(item.price, currencyCode)}
                            </span>
                          </>
                        )
                        : applyRoundAndFormat(item.price, currencyCode)
                      }
                      {orderDetails?.status === OrderStatus.Review && hasPermissionTo(['PriceOverrideWrite']) &&
                          <IconButton
                            size="small"
                            disabled={!canEditOrder()}
                            onClick={() => handleEditClick(item)}
                            sx={{ ml: 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                      }
                      {item.priceOverrideOriginalPrice == undefined && item.gbpPrice !== undefined && item.price !== item.gbpPrice && (
                        <span
                          style={{
                            color: theme.palette.text.disabled,
                            fontStyle: 'italic',
                            marginLeft: 6,
                            fontSize: '0.95em',
                          }}
                        >
                            ({applyRoundAndFormat(item.gbpPrice, 'GBP')})
                        </span>
                      )}
                    </Box>
                  )}
              </TableCell>
              <TableCell align="center"
                sx={{ verticalAlign: 'middle' }}>
                <QuantityCell
                  item={item}
                  initialQuantity={initialQuantity}
                  canChange={canChangeOrderQuantity()}
                  isQuantityError={isQuantityError}
                  quantityError={quantityError}
                  setQuantityInRef={setQuantityInRef}
                  autosaveItem={autosaveItem}
                  handleConfirmDeletion={handleConfirmDeletion}
                />
              </TableCell>
              <TableCell align="center"
                sx={{ verticalAlign: 'middle' }}>
                <span ref={priceCellRef}>{initialLinePrice}</span>
              </TableCell>
              <TableCell align="center"
                sx={{ verticalAlign: 'middle' }}>
                <Tooltip title={item.id === 0 ? t('cannotRemoveItem') : ''}>
                  <span>
                    <IconButton
                      color="error"
                      disabled={!canDeleteOrderItem() || item.id === 0}
                      onClick={() => handleOpenDialogForDeletion(item.id, item.itemId)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </>
          );
        }}
      />
      <Box mt={2}>
        <Table size="small">
          <TableBody>
            {filteredItems.length > 0 && (
              <>
                <TableRow>
                  <TableCell colSpan={4}
                    align="right"
                    sx={{ fontWeight: 'normal' }}>
                    {t('subtotal')}
                  </TableCell>
                  <TableCell align="center"
                    sx={{ fontWeight: 'normal' }}>
                    {applyRoundAndFormat(totalCost, currencyCode)}
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4}
                    align="right"
                    sx={{ fontWeight: 'normal' }}>
                    {t('deliveryCharge')}
                  </TableCell>
                  <TableCell align="center"
                    sx={{ fontWeight: 'normal' }}>
                    {applyRoundAndFormat(deliveryChargeTotal ? parseFloat(deliveryChargeTotal) : (parseFloat(containerCharge || '0') * computeContainerCount(containerData)), currencyCode)}
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                  <TableCell colSpan={4}
                    align="right"
                    sx={{ fontWeight: 'bold' }}>
                    {t('totalCost')}
                  </TableCell>
                  <TableCell align="center"
                    sx={{ fontWeight: 'bold' }}>
                    {applyRoundAndFormat(totalCost + (deliveryChargeTotal ? parseFloat(deliveryChargeTotal) : (parseFloat(containerCharge || '0') * computeContainerCount(containerData))), currencyCode)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </Box>
    </TableContainer >
  );
};

export default OrderTable;
